import { useState, useRef, useEffect, useCallback } from 'react';
import { ModalName } from 'features/Modals/ModalProvider';
import { selectRemoteParticipants, selectIsModalOpened, selectRoom } from 'state/selectors';
import { useModal } from 'useCase/modal/useModal';
import { isFeatureOn } from 'useCase/featureFlags/featureFlagsApi';
import { usePlayAudio } from '@web-chat-core/ui-kit/hooks/usePlayAudio';
import { FEATURES } from '../../../consts';
import { BEFORE_OPEN_CONFIRM_MODAL_TIME } from './consts';

interface UseLeaveRoomConfirmProps {
    onLeave: () => void;
}

export const useLeaveRoomConfirm = ({ onLeave }: UseLeaveRoomConfirmProps) => {
    const isContinueCallConfirmOpen = selectIsModalOpened(ModalName.CONTINUE_CALL_CONFIRM);
    const remoteParticipants = selectRemoteParticipants();
    const room = selectRoom();

    const [lastRemoteParticipantsSize, setLastRemoteParticipantsSize] = useState<number>(remoteParticipants.size);

    const { openModal, closeModal } = useModal();
    const { play, stop } = usePlayAudio('./nobody-in-room.mp3');

    const isAloneInCallNotificationEnabled = isFeatureOn(FEATURES.VCS_ALONE_IN_CALL_NOTIFICATION_ENABLED);

    const leaveRef = useRef<NodeJS.Timeout | null>(null);

    const startTimer = useCallback(() => {
        if (!leaveRef.current) {
            leaveRef.current = setTimeout(() => {
                openModal(ModalName.CONTINUE_CALL_CONFIRM, {
                    confirmCallback: () => {
                        clearTimer();
                        startTimer();
                    },
                    declineCallback: () => {
                        room?.disconnect();
                        onLeave();
                    },
                });
            }, BEFORE_OPEN_CONFIRM_MODAL_TIME);
        }
    }, [room, onLeave]);

    const clearTimer = () => {
        if (leaveRef.current) {
            clearTimeout(leaveRef.current);
            leaveRef.current = null;
        }
    };

    useEffect(
        function startTimerForLastUserOnCall() {
            if (!isAloneInCallNotificationEnabled) return;

            if (remoteParticipants.size === lastRemoteParticipantsSize) return;

            // Кейс, когда остальные пользователи покинули мит, и остался 1 участник.
            // Запускаем таймер на открытие модалки с вопросом, продолжать ли мит или нет.
            if (remoteParticipants.size === 0 && lastRemoteParticipantsSize > 0) {
                play();
                startTimer();
            }

            // Кейс, когда участник был один, и кто-то зашел в мит.
            if (remoteParticipants.size > 0) {
                // Если открыта модалка с вопросом о продолжении, то закрываем её.
                if (isContinueCallConfirmOpen) {
                    closeModal();
                }
                // Если добавился 1-ый участник, когда мы уже были подключены к миту, то останавливаем звук.
                if (remoteParticipants.size === 1 && lastRemoteParticipantsSize === 0) {
                    stop();
                }
                // Сбрасываем таймер.
                clearTimer();
            }

            // Обновляем размер участников для корректной работы предыдущей логики
            // при следующих изменениях количества участников.
            setLastRemoteParticipantsSize(remoteParticipants.size);
        },
        [
            isAloneInCallNotificationEnabled,
            remoteParticipants.size,
            lastRemoteParticipantsSize,
            isContinueCallConfirmOpen,
            startTimer,
        ],
    );

    useEffect(() => {
        // Чистим таймер и останавливаем звук при размонтировании компонента.
        return () => {
            stop();
            clearTimer();
        };
    }, []);
};
